import React, { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import * as yup from "yup";

import AccessComponent from "../../../../../../components/Generic/Access";
import ActionButtons from "../../../../../../components/Generic/ActionButtons";
import LoadingModal from "../../../../../../components/LoadingModal";
import { ExternalList } from "../../../../../../context/MovingResources";
import { useAppState } from "../../../../../../context/StateContext";
import { Options } from "../../../../../../models/access";
import { DifferentLocation } from "../../../../../../models/location";
import { Sales as SalesQuote } from "../../../../../../models/sales";
import AccessService from "../../../../../../services/access.service";
import { Status } from "../../../../../../util/enums";
import useStyles from "./styles";

type Address = {
  origin: DifferentLocation[];
  destination: DifferentLocation[];
};

interface Props {
  isOnViewMode?: boolean,
  data?: SalesQuote;
  addresses?: Address;
  isUpdating?: boolean;
  errors?: yup.ValidationError[];
  externalList?: ExternalList;
  isSavingAndSending?: boolean;
  handleUpdate: (attribute: string, value: any) => void;
  onCancel: () => void;
  onSave: () => void;
  onSaveAndSend?: () => void;
}

const AccessTab = ({
  isOnViewMode = false,
  data,
  addresses: _addresses,
  isUpdating,
  errors,
  externalList,
  isSavingAndSending = false,
  handleUpdate,
  onCancel,
  onSave,
  onSaveAndSend,
}: Props) => {
  const classes = useStyles();
  const { currentUser } = useAppState();

  const [drivewayLength, setDrivewayLength] = useState<Options[]>(
    externalList?.drivewayLengthList || []
  );
  const [parkings, setParkings] = useState<Options[]>(externalList?.parkingList || []);

  const [carryDistance, setCarryDistance] = useState<Options[]>(
    externalList?.carryDistanceList || []
  );
  const [accessActions, setAccessActions] = useState<Options[]>(
    externalList?.accessActionList || []
  );
  const [drivewayList, setDrivewayList] = useState<Options[]>(externalList?.drivewayList || []);

  useQuery(
    ["drivewayList"],
    () =>
      AccessService.getAllDriveways({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !externalList?.drivewayList,
      onSuccess: (response) => {
        if (response && response.data) {
          setDrivewayList(response.data);
        }
      },
    }
  );

  useQuery(
    ["drivewayLength"],
    () =>
      AccessService.getAllDrivewayLengths({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["id", "ASC"]]),
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !externalList?.drivewayLengthList,
      onSuccess: (response) => {
        if (response && response.data) {
          setDrivewayLength(response.data);
        }
      },
    }
  );

  useQuery(
    ["parkings"],
    () =>
      AccessService.getAllParkings({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !externalList?.parkingList,
      onSuccess: (response) => {
        if (response && response.data) {
          setParkings(response.data);
        }
      },
    }
  );

  useQuery(
    ["carryDistance"],
    () =>
      AccessService.getAllCarryDistances({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !externalList?.carryDistanceList,
      onSuccess: (response) => {
        if (response && response.data) {
          setCarryDistance(response.data);
        }
      },
    }
  );

  useQuery(
    ["accessActions"],
    () =>
      AccessService.getAllActions({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !externalList?.accessActionList,
      onSuccess: (response) => {
        if (response && response.data) {
          setAccessActions(response.data);
        }
      },
    }
  );

  return (
    <div data-cy="accessComponentBox">
      <div className={classes.container}>
        <AccessComponent
          data={data}
          handleUpdate={handleUpdate}
          selectOptions={{
            drivewayLength,
            parkings,
            carryDistance,
            accessActions,
            drivewayList,
          }}
          errors={errors}
        />
        {!isOnViewMode && (<div className={classes.buttons}>
          <ActionButtons
            showSaveAndSendButton={isSavingAndSending}
            cancelDisabled={isUpdating}
            saveDisabled={isUpdating}
            onCancel={onCancel}
            onSave={onSave}
            onSaveAndSend={onSaveAndSend}
          />
        </div>)}
      </div>
      <LoadingModal showing={isUpdating} />
    </div>
  );
};

export default AccessTab;
